// frontend/src/api/client.js
// All API calls go through here. JWT is auto-attached + auto-refreshed via Amplify.

import { fetchAuthSession } from "aws-amplify/auth";

const API = process.env.REACT_APP_API_ENDPOINT;

// Read Cognito ID token from localStorage (where aws-amplify stores it).
// Sync read — used for the first attempt before falling back to fetchAuthSession on 401.
const getTokenSync = () => {
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
  const prefix = `CognitoIdentityServiceProvider.${clientId}`;
  const lastUser = localStorage.getItem(`${prefix}.LastAuthUser`);
  if (!lastUser) return null;
  return localStorage.getItem(`${prefix}.${lastUser}.idToken`);
};

// Ask Amplify for a fresh ID token. Will refresh if expired.
const getFreshToken = async () => {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
};

// apiFetch — drop-in replacement for fetch() against our JWT-protected API.
// Auto-attaches the Cognito ID token, and on 401 will refresh the session once
// and retry the request before bubbling the error up. This means stale tokens
// in localStorage (the cause of intermittent 401s) self-heal without breaking
// the UX. Caller passes the same options object as fetch — minus headers, which
// this wrapper sets. Extra headers can still be added via options.extraHeaders.
const apiFetch = async (url, options = {}) => {
  const { extraHeaders, ...rest } = options;
  const send = (token) => fetch(url, {
    ...rest,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(extraHeaders || {}),
    },
  });

  let res = await send(getTokenSync());
  if (res.status === 401) {
    const fresh = await getFreshToken();
    if (fresh) res = await send(fresh);
  }
  return res;
};

const handleResponse = async (res) => {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.code   = data.errorCode || null;
    err.status = res.status;
    throw err;
  }
  return data;
};

// ── Transactions ──────────────────────────────────────────────────────────────
export const transactionsApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v))
    ).toString();
    return apiFetch(`${API}/transactions${qs ? `?${qs}` : ""}`).then(handleResponse);
  },

  create: (txn) =>
    apiFetch(`${API}/transactions`, {
      method: "POST",
      body: JSON.stringify(txn),
    }).then(handleResponse),

  delete: (id, txnDate) =>
    apiFetch(`${API}/transactions/${id}?txnDate=${txnDate}`, {
      method: "DELETE",
    }).then(handleResponse),

  listPending: () =>
    apiFetch(`${API}/transactions?status=pending&limit=50`).then(handleResponse),

  listScheduled: () =>
    apiFetch(`${API}/transactions?status=scheduled&limit=50`).then(handleResponse),

  confirm: (id, txnDate, overrides = {}) =>
    apiFetch(`${API}/transactions/${id}/confirm?txnDate=${txnDate}`, {
      method: "POST",
      body: JSON.stringify(overrides),
    }).then(handleResponse),

  setEssential: (id, txnDate, isEssential) =>
    apiFetch(`${API}/transactions/${id}/essential?txnDate=${txnDate}`, {
      method: "POST",
      body: JSON.stringify({ isEssential }),
    }).then(handleResponse),
};

// ── Category Summaries ────────────────────────────────────────────────────────
export const categoriesApi = {
  getSummaries: (year) => {
    const qs = year ? `?year=${year}` : "";
    return apiFetch(`${API}/categories${qs}`).then(handleResponse);
  },

  getTrends: (months = 12) =>
    apiFetch(`${API}/categories/trends?months=${months}`).then(handleResponse),

  deleteAllMyData: () =>
    apiFetch(`${API}/categories/me`, {
      method: "DELETE",
      extraHeaders: { "x-confirm-delete": "DELETE_ALL_MY_DATA" },
    }).then(handleResponse),
};

// ── Family Profile ────────────────────────────────────────────────────────────
export const profileApi = {
  getFamily: () =>
    apiFetch(`${API}/profile/family`).then(handleResponse),

  saveFamily: (members) =>
    apiFetch(`${API}/profile/family`, {
      method: "PUT",
      body: JSON.stringify({ members }),
    }).then(handleResponse),

  createInvite: (email) =>
    apiFetch(`${API}/profile/invite`, {
      method: "POST",
      body: JSON.stringify({ email }),
    }).then(handleResponse),

  joinFamily: (inviteCode) =>
    apiFetch(`${API}/profile/join`, {
      method: "POST",
      body: JSON.stringify({ inviteCode }),
    }).then(handleResponse),

  unlink: () =>
    apiFetch(`${API}/profile/link`, { method: "DELETE" }).then(handleResponse),

  getInvestments: () =>
    apiFetch(`${API}/profile/investments`).then(handleResponse),

  saveInvestments: (prefs) =>
    apiFetch(`${API}/profile/investments`, {
      method: "PUT",
      body: JSON.stringify(prefs),
    }).then(handleResponse),

  getGoals: () =>
    apiFetch(`${API}/profile/goals`).then(handleResponse),

  saveGoals: (goals) =>
    apiFetch(`${API}/profile/goals`, {
      method: "PUT",
      body: JSON.stringify({ goals }),
    }).then(handleResponse),

  getEmailAlias: () =>
    apiFetch(`${API}/profile/email-alias`).then(handleResponse),

  getHoldings: () =>
    apiFetch(`${API}/profile/holdings`).then(handleResponse),

  createHolding: (data) =>
    apiFetch(`${API}/profile/holdings`, {
      method: "POST",
      body: JSON.stringify(data),
    }).then(handleResponse),

  updateHolding: (isin, updates) =>
    apiFetch(`${API}/profile/holdings/${encodeURIComponent(isin)}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    }).then(handleResponse),

  deleteHolding: (isin) =>
    apiFetch(`${API}/profile/holdings/${encodeURIComponent(isin)}`, {
      method: "DELETE",
    }).then(handleResponse),

  getMessages: () =>
    apiFetch(`${API}/profile/messages`).then(handleResponse),

  dismissMessage: (sk) =>
    apiFetch(`${API}/profile/messages/${encodeURIComponent(sk)}`, {
      method: "DELETE",
    }).then(handleResponse),
};

// ── AI Analysis ───────────────────────────────────────────────────────────────
export const aiApi = {
  analyze: (analysisType, snapshot) =>
    apiFetch(`${API}/ai/analyze`, {
      method: "POST",
      body: JSON.stringify({ analysisType, snapshot }),
    }).then(handleResponse),

  getUsage: () =>
    apiFetch(`${API}/profile/ai-usage`).then(handleResponse),
};

// ── Receipts (presigned S3) ─────────────────────────────────────────────────
export const receiptsApi = {
  getUploadUrl: (contentType) =>
    apiFetch(`${API}/receipts/upload-url`, {
      method: "POST",
      body: JSON.stringify({ contentType }),
    }).then(handleResponse),

  // Upload a blob directly to S3 using the presigned PUT URL. Pure XHR — no auth header,
  // signature is baked into the URL. Reports progress via onProgress callback.
  uploadToS3: (url, blob, onProgress) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", blob.type || "image/jpeg");
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () => xhr.status >= 200 && xhr.status < 300
      ? resolve()
      : reject(new Error(`S3 upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error("Network error uploading to S3"));
    xhr.send(blob);
  }),

  getViewUrl: (key) =>
    apiFetch(`${API}/receipts/view-url?key=${encodeURIComponent(key)}`).then(handleResponse),
};

// ── Admin API (Basic Auth, separate from Cognito — no auto-refresh) ─────────
const ADMIN_CRED_KEY = "trackfin.admin.creds";

const adminBasicHeader = () => {
  const creds = sessionStorage.getItem(ADMIN_CRED_KEY) || localStorage.getItem(ADMIN_CRED_KEY);
  return creds ? { Authorization: `Basic ${creds}` } : {};
};

export const adminApi = {
  isLoggedIn: () => !!(sessionStorage.getItem(ADMIN_CRED_KEY) || localStorage.getItem(ADMIN_CRED_KEY)),

  login: async (username, password, remember = false) => {
    const res = await fetch(`${API}/admin/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `Login failed (HTTP ${res.status})`);
    }
    const creds = btoa(`${username}:${password}`);
    (remember ? localStorage : sessionStorage).setItem(ADMIN_CRED_KEY, creds);
    return true;
  },

  logout: () => {
    sessionStorage.removeItem(ADMIN_CRED_KEY);
    localStorage.removeItem(ADMIN_CRED_KEY);
  },

  diagnostics: () =>
    fetch(`${API}/admin/diagnostics`, { headers: adminBasicHeader() }).then(handleResponse),

  lambdaFunctions: () =>
    fetch(`${API}/admin/lambda-fns`, { headers: adminBasicHeader() }).then(handleResponse),

  lambdaLogs: ({ fn = "email-parser", hours = 24, filter = "" } = {}) => {
    const q = new URLSearchParams({ fn, hours: String(hours), filter });
    return fetch(`${API}/admin/lambda-logs?${q}`, { headers: adminBasicHeader() }).then(handleResponse);
  },

  pendingEmails: () =>
    fetch(`${API}/admin/pending-emails`, { headers: adminBasicHeader() }).then(handleResponse),

  reprocessEmail: (s3Key) =>
    fetch(`${API}/admin/reprocess-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...adminBasicHeader() },
      body: JSON.stringify({ s3Key }),
    }).then(handleResponse),

  activity: (limit = 50) =>
    fetch(`${API}/admin/activity?limit=${limit}`, { headers: adminBasicHeader() }).then(handleResponse),
};
